/**
 * DownloadDialog Component
 * Modal dialog for selecting download format with size preview
 * Routes to client-side ZIP or manifest dialog based on estimated size
 */

import { useState, useEffect, useMemo } from "react";
import { Modal, Radio, Space, Typography, Alert, Button, Tag } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { MODAL_BLUR_STYLES } from "@/styles/modalStyles";
import {
  DownloadFormat,
  hasRawFiles,
  estimateDownloadSize,
  getDownloadLevel,
  isMobileDevice,
  probePhotoSizes,
  SIZE_THRESHOLDS,
} from "@/utils/downloadUtils";
import type { Photo } from "@/types";

const { Text } = Typography;

interface DownloadDialogProps {
  open: boolean;
  photos: Photo[];
  onDownload: (format: DownloadFormat) => void;
  onManifestDownload?: (format: DownloadFormat) => void;
  onCancel: () => void;
}

interface FormatOption {
  value: DownloadFormat;
  label: string;
  icon: string;
  sizeMB: number;
  fileCount: number;
  available: boolean;
  availableCount?: number;
  totalCount?: number;
}

export function DownloadDialog({
  open,
  photos,
  onDownload,
  onManifestDownload,
  onCancel,
}: DownloadDialogProps) {
  const [selectedFormat, setSelectedFormat] = useState<DownloadFormat>("jpeg");

  const [sizesProbed, setSizesProbed] = useState(false);

  // Reset format only when dialog opens (not on photos reference change)
  useEffect(() => {
    if (open) {
      setSelectedFormat("jpeg");
    }
  }, [open]);

  // Probe sizes separately — doesn't reset selection
  useEffect(() => {
    if (open) {
      probePhotoSizes(photos).then(() => setSizesProbed(true));
    }
  }, [open, photos]);

  const isMobile = isMobileDevice();

  // Calculate format options with sizes (re-calculated after probe completes)
  const formatOptions = useMemo((): FormatOption[] => {
    const hasRaw = hasRawFiles(photos);
    const rawCount = photos.filter((p) => Boolean(p.rawUrl)).length;
    const jpegEstimate = estimateDownloadSize(photos, "jpeg");
    const rawEstimate = estimateDownloadSize(photos, "raw");
    const bothEstimate = estimateDownloadSize(photos, "both");

    return [
      {
        value: "jpeg",
        label: "JPEG only",
        icon: "image",
        sizeMB: jpegEstimate.sizeMB,
        fileCount: jpegEstimate.fileCount,
        available: true,
      },
      {
        value: "raw",
        label: "RAW only",
        icon: "raw_on",
        sizeMB: rawEstimate.sizeMB,
        fileCount: rawEstimate.fileCount,
        available: hasRaw,
        availableCount: rawCount,
        totalCount: photos.length,
      },
      {
        value: "both",
        label: "JPEG + RAW",
        icon: "filter_2",
        sizeMB: bothEstimate.sizeMB,
        fileCount: bothEstimate.fileCount,
        available: hasRaw,
        availableCount: rawCount,
        totalCount: photos.length,
      },
    ];
  }, [photos, sizesProbed]);

  // Get selected format details
  const selectedOption = formatOptions.find((o) => o.value === selectedFormat);
  const selectedSizeMB = selectedOption?.sizeMB ?? 0;
  const downloadLevel = getDownloadLevel(selectedSizeMB);

  // Format size for display
  const formatSize = (mb: number) => {
    if (mb >= 1000) {
      return `${(mb / 1000).toFixed(1)} GB`;
    }
    return `${mb} MB`;
  };

  const handleDownload = () => {
    if (downloadLevel === "manifest" && onManifestDownload) {
      onManifestDownload(selectedFormat);
    } else {
      onDownload(selectedFormat);
    }
  };

  // Download method label for button
  const getDownloadMethodInfo = () => {
    if (downloadLevel === "manifest") {
      return { label: "Download script", color: "orange" as const };
    }
    return { label: "Browser download", color: "green" as const };
  };

  const methodInfo = getDownloadMethodInfo();

  return (
    <Modal
      title={
        <Space>
          <MaterialIcon name="download" />
          <span>Download {photos.length} photo{photos.length > 1 ? "s" : ""}</span>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      styles={MODAL_BLUR_STYLES}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          {!isMobile && (
            <Button
              type="primary"
              onClick={handleDownload}
              icon={<MaterialIcon name={downloadLevel === "manifest" ? "terminal" : "download"} size={16} />}
            >
              {downloadLevel === "manifest"
                ? "Get download script"
                : `Download ${formatSize(selectedSizeMB)}`}
            </Button>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {isMobile ? (
          <Alert
            type="warning"
            showIcon
            message="Desktop required"
            description="Bulk photo downloads are only available on desktop browsers. Please open the dashboard on a computer to download photos."
          />
        ) : (
          <>
            <div>
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Select format to download:
              </Text>
              <Radio.Group
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {formatOptions.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      disabled={!option.available}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                        backgroundColor:
                          selectedFormat === option.value ? "#f0f5ff" : "white",
                      }}
                    >
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          display: "flex",
                        }}
                      >
                        <Space>
                          <MaterialIcon name={option.icon} size={20} />
                          <span>{option.label}</span>
                          {option.availableCount !== undefined &&
                            option.availableCount < (option.totalCount ?? 0) && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                ({option.availableCount} of {option.totalCount} have RAW)
                              </Text>
                            )}
                        </Space>
                        <Text
                          type={option.available ? undefined : "secondary"}
                          style={{ fontWeight: 500 }}
                        >
                          {option.available
                            ? `~${formatSize(option.sizeMB)}`
                            : "Not available"}
                        </Text>
                      </Space>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </div>

            {/* Download method indicator */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text type="secondary">Method:</Text>
                <Tag color={methodInfo.color}>{methodInfo.label}</Tag>
              </div>
              {downloadLevel === "manifest" && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Too large for browser — a download script will be generated
                </Text>
              )}
            </div>

            {downloadLevel === "manifest" && (
              <Alert
                type="info"
                showIcon
                icon={<MaterialIcon name="terminal" />}
                message="Large download"
                description={`This download (~${formatSize(selectedSizeMB)}) exceeds ${formatSize(SIZE_THRESHOLDS.CLIENT_ZIP_MAX_MB)}. A download script will be generated that you can run on your computer to download all files with resume support.`}
              />
            )}
          </>
        )}
      </Space>
    </Modal>
  );
}
